/**
 * The topbar's breadcrumb. Renders whatever the current page published through
 * `useBreadcrumb`; nothing at all when a page publishes nothing.
 *
 * Crumbs are buttons, not links: `setHandleRedirect` is what lets a page with
 * unsaved work (the builder answer editor) prompt before the route changes.
 *
 * They route through `ButtonBase` rather than a bare `<button>`. The theme has
 * a global `button:not(.MuiButtonBase-root, …)` rule that forces 0.8em text, a
 * 3px radius and a focus underline onto any unadorned button — which is what a
 * plain `Link component="button"` renders as. `ButtonBase` is named in that
 * rule's exclusion list, so the crumb keeps the design's own type.
 */
import { Box, ButtonBase, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setHandleRedirect } from "@/store/sideNav/sideNavActions";
import radii from "@/theme/radii";
import tokens from "@/theme/tokens";
import { useBreadcrumbTrail } from "./BreadcrumbContext";

export default function HeaderBreadcrumb() {
  const { t } = useTranslation(["translation.navigation"]);
  const dispatch = useDispatch();
  const trail = useBreadcrumbTrail();

  if (!trail.length) return null;

  return (
    <Box
      component="nav"
      aria-label={String(t("Breadcrumb"))}
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        px: 1,
      }}
    >
      {trail.map((crumb, index) => {
        const isLast = index === trail.length - 1;
        return (
          <Box
            key={`${crumb.label}-${crumb.href ?? "current"}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            {index > 0 && (
              <Typography
                variant="body2"
                aria-hidden
                sx={{ color: tokens.color.inputBorder }}
              >
                /
              </Typography>
            )}
            {isLast || !crumb.href ? (
              <Typography
                variant="body2"
                aria-current="page"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <ButtonBase
                disableRipple
                onClick={() =>
                  dispatch(
                    setHandleRedirect(
                      crumb.projectId ?? null,
                      crumb.href as string,
                      crumb.hubId
                    )
                  )
                }
                sx={{ borderRadius: radii.none }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {crumb.label}
                </Typography>
              </ButtonBase>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
